import type { LessonUpdatePayload } from './interfaces'

import { resolveUrl } from '@shared/lib/url'
import { AppRoute } from '@shared/config'
import { useToast } from '@shared/ui/toast'

import { useLessonEditStore } from './edit/store'
import { useLessonUpdateApi } from '../../api/lessons/update.hook'
import { useDisciplineRefresh } from '../discipline-refresh.hook'
import { isLessonReasonStatus } from './lesson-status'

interface LessonEditSaveReturn {
  refIsTutorChanged: ComputedRef<boolean>
  refLessonsUrl: string
  save: () => Promise<boolean>
}

/** The one save of the edit page, from its header or from the tutor modal: only what the admin changed is sent, a
 *  cancel, reschedule or no-show status always goes with its reason, and a replaced tutor with the reason given. */
export const useLessonEditSave = (): LessonEditSaveReturn => {
  const route = useRoute()
  const toast = useToast()
  const store = useLessonEditStore()
  const { refreshDiscipline } = useDisciplineRefresh()

  const disciplineId = route.params.id as string

  const refLessonsUrl = resolveUrl(AppRoute.DisciplinesLessons, { id: disciplineId }) ?? ''

  /** Only replacing an assigned tutor needs a reason; giving a lesson its first tutor does not. */
  const refIsTutorChanged = computed<boolean>(() => {
    const current = store.getLesson?.tutor_id ?? null

    return current !== null && store.getTutorId !== current
  })

  const buildPayload = (): LessonUpdatePayload => {
    const lesson = store.getLesson
    if (!lesson) return {}

    const payload: LessonUpdatePayload = {}
    const status = store.getStatus

    if (store.getStart && store.getStart !== lesson.start) payload.start = store.getStart
    if (store.getDuration && store.getDuration !== lesson.duration) payload.duration = store.getDuration

    if (status && isLessonReasonStatus(status)) {
      payload.status = status
      payload.reason = store.getReason
      payload.reason_category = store.getReasonCategory
    } else if (status && status !== lesson.status) {
      payload.status = status
      payload.reason = null
      payload.reason_category = null
    }

    if (store.getTutorId !== lesson.tutor_id) {
      payload.tutor_id = store.getTutorId

      if (refIsTutorChanged.value) {
        payload.tutor_reassign_reason = store.getReassignReason
        payload.tutor_reassign_comment = store.getReassignComment.trim() || null
      }
    }

    return payload
  }

  const save = async (): Promise<boolean> => {
    const lesson = store.getLesson
    if (!lesson) return false

    const payload = buildPayload()

    if (!Object.keys(payload).length) {
      toast.info('There are no changes to save.')
      return false
    }

    const isUpdated = await useLessonUpdateApi({
      disciplineId,
      lessonId: lesson.id,
      payload,
      onStart: () => store.setSaving(true),
      onFinish: () => store.setSaving(false),
    })

    if (!isUpdated) return false

    // The discipline is reread once the Lessons tab is up: its page loader covers that read, whereas here it would
    // rise over the open modal.
    await navigateTo(refLessonsUrl)
    void refreshDiscipline()

    return true
  }

  return {
    refIsTutorChanged,
    refLessonsUrl,
    save,
  }
}
